import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Store, Upload, Save, ExternalLink, Check, Palette, Lock } from 'lucide-react';

const THEMES = [
  { id: 'classique', name: 'Classique', headerBg: '#FFFFFF', pageBg: '#F9FAFB', cardBg: '#FFFFFF', text: '#111827', accent: '#3B82F6' },
  { id: 'sombre', name: 'Sombre', headerBg: '#18181B', pageBg: '#09090B', cardBg: '#18181B', text: '#FAFAFA', accent: '#F59E0B' },
  { id: 'elegant', name: 'Elegant', headerBg: '#1C1917', pageBg: '#FAFAF9', cardBg: '#FFFFFF', text: '#1C1917', accent: '#B45309' },
  { id: 'nature', name: 'Nature', headerBg: '#14532D', pageBg: '#F0FDF4', cardBg: '#FFFFFF', text: '#14532D', accent: '#16A34A' },
  { id: 'ocean', name: 'Ocean', headerBg: '#0C4A6E', pageBg: '#F0F9FF', cardBg: '#FFFFFF', text: '#0C4A6E', accent: '#0284C7' },
  { id: 'rose', name: 'Rose', headerBg: '#831843', pageBg: '#FDF2F8', cardBg: '#FFFFFF', text: '#831843', accent: '#DB2777' },
  { id: 'violet', name: 'Violet', headerBg: '#4C1D95', pageBg: '#F5F3FF', cardBg: '#FFFFFF', text: '#4C1D95', accent: '#7C3AED' },
  { id: 'custom', name: 'Personnalise', headerBg: null, pageBg: null, cardBg: null, text: null, accent: null },
];

const ShopSettings = () => {
  const { shopId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    whatsappNumber: '',
    address: '',
    hoursText: '',
    accentColor: '#3B82F6',
    theme: 'classique',
    headerColor: null,
    backgroundColor: null,
    cardColor: null,
    textColor: null,
    isPublished: false,
    instagram: '',
    facebook: '',
    website: '',
    slogan: '',
    tiktok: '',
    googleReviewUrl: ''
  });

  useEffect(() => {
    fetchShop();
  }, [shopId]);

  const fetchShop = async () => {
    try {
      const response = await api.get(`/shops/${shopId}`);
      setShop(response.data);
      setFormData({
        name: response.data.name || '',
        phone: response.data.phone || '',
        whatsappNumber: response.data.whatsappNumber || '',
        address: response.data.address || '',
        hoursText: response.data.hoursText || '',
        accentColor: response.data.accentColor || '#3B82F6',
        theme: response.data.theme || 'classique',
        headerColor: response.data.headerColor || null,
        backgroundColor: response.data.backgroundColor || null,
        cardColor: response.data.cardColor || null,
        textColor: response.data.textColor || null,
        isPublished: response.data.isPublished || false,
        instagram: response.data.instagram || '',
        facebook: response.data.facebook || '',
        website: response.data.website || '',
        slogan: response.data.slogan || '',
        tiktok: response.data.tiktok || '',
        googleReviewUrl: response.data.googleReviewUrl || ''
      });
    } catch (error) {
      toast.error('Boutique non trouvee');
      navigate('/app/shops');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.put(`/shops/${shopId}`, formData);
      setShop(response.data);
      toast.success('Modifications enregistrees !');
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const response = await api.post(`/shops/${shopId}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShop({ ...shop, logoUrl: response.data.logoUrl });
      toast.success('Logo mis a jour !');
    } catch (error) {
      toast.error('Erreur lors du telechargement');
    } finally {
      setUploading(false);
    }
  };

  const selectTheme = (theme) => {
    if (theme.id === 'custom') {
      setFormData(prev => ({ ...prev, theme: 'custom' }));
    } else {
      setFormData(prev => ({
        ...prev,
        theme: theme.id,
        accentColor: theme.accent,
        headerColor: theme.headerBg,
        backgroundColor: theme.pageBg,
        cardColor: theme.cardBg,
        textColor: theme.text,
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const canCustomizeTheme = user?.plan === 'PRO' || user?.plan === 'PREMIUM';

  return (
    <div className="space-y-6 max-w-2xl" data-testid="shop-settings">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Parametres</h1>
        <p className="text-muted-foreground mt-1">{shop?.name}</p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Logo */}
        <Card>
          <CardHeader>
            <CardTitle>Logo</CardTitle>
            <CardDescription>L'image de votre boutique</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              {shop?.logoUrl ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${shop.logoUrl}`} alt={shop.name} className="w-24 h-24 rounded-xl object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-muted flex items-center justify-center">
                  <Store className="w-10 h-10 text-muted-foreground" />
                </div>
              )}
              <div>
                <input type="file" id="logo" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                <Button type="button" variant="outline" onClick={() => document.getElementById('logo').click()} disabled={uploading}>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Envoi...' : 'Changer le logo'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Informations */}
        <Card>
          <CardHeader>
            <CardTitle>Informations</CardTitle>
            <CardDescription>Les informations de votre boutique</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom de la boutique</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="shop-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Telephone</Label>
              <Input id="phone" type="tel" placeholder="+33 1 23 45 67 89" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="shop-phone-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="whatsapp">Numero WhatsApp</Label>
              <Input id="whatsapp" type="tel" placeholder="+33612345678" value={formData.whatsappNumber} onChange={(e) => setFormData({ ...formData, whatsappNumber: e.target.value })} data-testid="shop-whatsapp-input" />
              <p className="text-xs text-muted-foreground">Format international sans espaces</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Adresse</Label>
              <Textarea id="address" placeholder="123 Rue du Commerce, 75001 Paris" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} data-testid="shop-address-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hours">Horaires d'ouverture</Label>
              <Textarea id="hours" placeholder="Lun-Sam: 9h-19h" value={formData.hoursText} onChange={(e) => setFormData({ ...formData, hoursText: e.target.value })} data-testid="shop-hours-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slogan">Slogan / Message d'accroche</Label>
              <Input id="slogan" placeholder="Ex: La mode qui vous ressemble" value={formData.slogan} onChange={(e) => setFormData({ ...formData, slogan: e.target.value })} data-testid="shop-slogan-input" />
              <p className="text-xs text-muted-foreground">Affiche sur la couverture de votre catalogue PDF</p>
            </div>
          </CardContent>
        </Card>

        {/* Reseaux sociaux */}
        <Card>
          <CardHeader>
            <CardTitle>Reseaux sociaux</CardTitle>
            <CardDescription>Affiches dans votre catalogue PDF</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="instagram">Instagram</Label>
              <Input id="instagram" placeholder="@votreboutique" value={formData.instagram} onChange={(e) => setFormData({ ...formData, instagram: e.target.value })} data-testid="shop-instagram-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="facebook">Facebook</Label>
              <Input id="facebook" placeholder="facebook.com/votreboutique" value={formData.facebook} onChange={(e) => setFormData({ ...formData, facebook: e.target.value })} data-testid="shop-facebook-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Site web</Label>
              <Input id="website" placeholder="www.votreboutique.com" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} data-testid="shop-website-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tiktok">TikTok</Label>
              <Input id="tiktok" placeholder="@votreboutique" value={formData.tiktok} onChange={(e) => setFormData({ ...formData, tiktok: e.target.value })} data-testid="shop-tiktok-input" />
            </div>
            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="googleReviewUrl" className="flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Lien avis Google
              </Label>
              <Input id="googleReviewUrl" placeholder="https://g.page/r/..." value={formData.googleReviewUrl} onChange={(e) => setFormData({ ...formData, googleReviewUrl: e.target.value })} data-testid="shop-google-review-input" />
              <p className="text-xs text-muted-foreground">Une bulle invitera vos clients a laisser un avis Google sur votre page publique</p>
            </div>
          </CardContent>
        </Card>

        {/* Themes */}
        <Card className={!canCustomizeTheme ? 'opacity-75' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Themes et couleurs
              {!canCustomizeTheme && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full flex items-center gap-1">
                  <Lock className="w-3 h-3" /> PRO
                </span>
              )}
            </CardTitle>
            <CardDescription>Choisissez un theme predefini ou personnalisez les couleurs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {!canCustomizeTheme && (
              <p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                Passez au plan PRO ou PREMIUM pour acceder aux themes et couleurs personnalisables.
              </p>
            )}

            {/* Theme grid */}
            <div>
              <Label className="mb-3 block">Themes predefinis</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {THEMES.map(theme => (
                  <ThemeCard
                    key={theme.id}
                    theme={theme}
                    isSelected={formData.theme === theme.id}
                    onSelect={() => canCustomizeTheme && selectTheme(theme)}
                    disabled={!canCustomizeTheme}
                  />
                ))}
              </div>
            </div>

            {/* Custom colors */}
            {canCustomizeTheme && (formData.theme === 'custom' || formData.theme) && (
              <div className="space-y-4 pt-2">
                <Label className="block">Couleurs</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ColorPicker label="Couleur d'accent" value={formData.accentColor} onChange={(v) => setFormData({ ...formData, accentColor: v })} testId="color-accent" />
                  <ColorPicker label="En-tete" value={formData.headerColor || '#FFFFFF'} onChange={(v) => setFormData({ ...formData, headerColor: v })} testId="color-header" />
                  <ColorPicker label="Arriere-plan" value={formData.backgroundColor || '#F9FAFB'} onChange={(v) => setFormData({ ...formData, backgroundColor: v })} testId="color-bg" />
                  <ColorPicker label="Cartes" value={formData.cardColor || '#FFFFFF'} onChange={(v) => setFormData({ ...formData, cardColor: v })} testId="color-card" />
                  <ColorPicker label="Texte" value={formData.textColor || '#111827'} onChange={(v) => setFormData({ ...formData, textColor: v })} testId="color-text" />
                </div>
              </div>
            )}

            {/* Preview */}
            {canCustomizeTheme && (
              <div>
                <Label className="mb-3 block">Apercu</Label>
                <ThemePreview
                  headerColor={formData.headerColor || '#FFFFFF'}
                  backgroundColor={formData.backgroundColor || '#F9FAFB'}
                  cardColor={formData.cardColor || '#FFFFFF'}
                  textColor={formData.textColor || '#111827'}
                  accentColor={formData.accentColor}
                  shopName={formData.name}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Publication */}
        <Card>
          <CardHeader>
            <CardTitle>Publication</CardTitle>
            <CardDescription>Rendez votre catalogue accessible au public</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Publier le catalogue</Label>
                <p className="text-sm text-muted-foreground">Votre catalogue sera accessible via l'URL publique</p>
              </div>
              <Switch checked={formData.isPublished} onCheckedChange={(checked) => setFormData({ ...formData, isPublished: checked })} data-testid="shop-publish-switch" />
            </div>
            {formData.isPublished && shop?.slug && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium mb-2">URL de votre catalogue :</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm bg-background px-3 py-2 rounded">{window.location.origin}/s/{shop.slug}</code>
                  <Button type="button" variant="outline" size="icon" onClick={() => window.open(`/s/${shop.slug}`, '_blank')}>
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} data-testid="save-shop-settings">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </form>
    </div>
  );
};

const ThemeCard = ({ theme, isSelected, onSelect, disabled }) => {
  if (theme.id === 'custom') {
    return (
      <button
        type="button"
        onClick={onSelect}
        disabled={disabled}
        className={`relative rounded-xl border-2 p-3 text-center transition-all ${
          isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/50'
        } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid="theme-custom"
      >
        <div className="w-full aspect-[4/3] rounded-lg mb-2 flex items-center justify-center bg-gradient-to-br from-pink-200 via-blue-200 to-green-200">
          <Palette className="w-6 h-6 text-gray-600" />
        </div>
        <span className="text-xs font-medium">{theme.name}</span>
        {isSelected && <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={`relative rounded-xl border-2 p-3 text-center transition-all ${
        isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border hover:border-primary/50'
      } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      data-testid={`theme-${theme.id}`}
    >
      <div className="w-full aspect-[4/3] rounded-lg mb-2 overflow-hidden" style={{ backgroundColor: theme.pageBg }}>
        <div className="h-1/3 w-full" style={{ backgroundColor: theme.headerBg }} />
        <div className="p-1.5 flex gap-1">
          <div className="flex-1 h-3 rounded-sm" style={{ backgroundColor: theme.cardBg, border: '1px solid rgba(0,0,0,0.08)' }} />
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: theme.accent }} />
        </div>
      </div>
      <span className="text-xs font-medium">{theme.name}</span>
      {isSelected && <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
    </button>
  );
};

const ColorPicker = ({ label, value, onChange, testId }) => (
  <div className="flex items-center gap-3">
    <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border shrink-0" data-testid={testId} />
    <div className="flex-1">
      <p className="text-sm font-medium">{label}</p>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs mt-1" />
    </div>
  </div>
);

const ThemePreview = ({ headerColor, backgroundColor, cardColor, textColor, accentColor, shopName }) => (
  <div className="rounded-xl overflow-hidden border shadow-sm" style={{ backgroundColor }} data-testid="theme-preview">
    <div className="px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: headerColor }}>
      <div className="w-6 h-6 rounded-md" style={{ backgroundColor: accentColor }} />
      <span className="text-sm font-semibold" style={{ color: textColor }}>{shopName || 'Ma Boutique'}</span>
    </div>
    <div className="p-3 space-y-2">
      <div className="rounded-lg p-3 flex items-center gap-3" style={{ backgroundColor: cardColor }}>
        <div className="w-10 h-10 rounded-md bg-gray-200" />
        <div className="flex-1">
          <div className="h-3 w-24 rounded" style={{ backgroundColor: textColor, opacity: 0.7 }} />
          <div className="h-2.5 w-16 rounded mt-1.5" style={{ backgroundColor: accentColor }} />
        </div>
      </div>
      <div className="rounded-lg p-3 flex items-center gap-3" style={{ backgroundColor: cardColor }}>
        <div className="w-10 h-10 rounded-md bg-gray-200" />
        <div className="flex-1">
          <div className="h-3 w-20 rounded" style={{ backgroundColor: textColor, opacity: 0.7 }} />
          <div className="h-2.5 w-12 rounded mt-1.5" style={{ backgroundColor: accentColor }} />
        </div>
      </div>
    </div>
  </div>
);

export default ShopSettings;
